import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import api from '../../lib/api'

interface AdminUser {
  id: string
  username: string
  displayName: string
  email: string
  avatarUrl?: string
  ageGroup: 'child' | 'teen' | 'adult'
  isVerified: boolean
  isActive: boolean
  isSuspended: boolean
  isBanned: boolean
  createdAt: string
  lastActiveAt: string
  postCount: number
  followerCount: number
  violationCount: number
  activeStrikes: number
}

interface UserDetails extends AdminUser {
  violations: {
    id: string
    type: string
    severity: string
    description: string
    createdAt: string
  }[]
  recentActivity: {
    type: string
    description: string
    timestamp: string
  }[]
  moderationStats: {
    contentBlocked: number
    contentFlagged: number
    reportsReceived: number
    reportsSubmitted: number
  }
}

export default function UserManagementPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [filter, setFilter] = useState<'all' | 'active' | 'suspended' | 'banned' | 'high_risk'>('all')

  useEffect(() => {
    fetchUsers()
  }, [filter, searchQuery])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const response = await api.get('/admin/users', {
        params: { filter, search: searchQuery, limit: 50 },
      })
      setUsers(response.data.users || [])
    } catch (error) {
      console.error('Failed to fetch users:', error)
      // Mock data
      setUsers([
        {
          id: '1',
          username: 'creative_artist',
          displayName: 'Creative Artist',
          email: 'artist@example.com',
          ageGroup: 'teen',
          isVerified: true,
          isActive: true,
          isSuspended: false,
          isBanned: false,
          createdAt: '2024-01-01T00:00:00Z',
          lastActiveAt: '2024-01-15T10:00:00Z',
          postCount: 234,
          followerCount: 1523,
          violationCount: 0,
          activeStrikes: 0,
        },
        {
          id: '2',
          username: 'problem_user',
          displayName: 'Problem User',
          email: 'problem@example.com',
          ageGroup: 'child',
          isVerified: false,
          isActive: true,
          isSuspended: false,
          isBanned: false,
          createdAt: '2024-01-10T00:00:00Z',
          lastActiveAt: '2024-01-15T09:00:00Z',
          postCount: 45,
          followerCount: 23,
          violationCount: 3,
          activeStrikes: 2,
        },
        {
          id: '3',
          username: 'suspended_user',
          displayName: 'Suspended User',
          email: 'suspended@example.com',
          ageGroup: 'adult',
          isVerified: true,
          isActive: false,
          isSuspended: true,
          isBanned: false,
          createdAt: '2023-12-01T00:00:00Z',
          lastActiveAt: '2024-01-14T00:00:00Z',
          postCount: 156,
          followerCount: 892,
          violationCount: 5,
          activeStrikes: 3,
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleUserSelect = async (userId: string) => {
    try {
      const response = await api.get(`/admin/users/${userId}`)
      setSelectedUser(response.data)
    } catch (error) {
      console.error('Failed to fetch user details:', error)
      // Mock data
      const user = users.find((u) => u.id === userId)
      if (user) {
        setSelectedUser({
          ...user,
          violations: [
            {
              id: 'v1',
              type: 'harassment',
              severity: 'moderate',
              description: 'Insulting comment on another user\'s post',
              createdAt: '2024-01-14T00:00:00Z',
            },
          ],
          recentActivity: [
            { type: 'post', description: 'Created a new post', timestamp: '2 hours ago' },
            { type: 'comment', description: 'Commented on a post', timestamp: '3 hours ago' },
          ],
          moderationStats: {
            contentBlocked: 2,
            contentFlagged: 5,
            reportsReceived: 3,
            reportsSubmitted: 1,
          },
        })
      }
    }
  }

  const handleUserAction = async (userId: string, action: 'suspend' | 'unsuspend' | 'ban' | 'unban' | 'warn' | 'verify') => {
    try {
      await api.post(`/admin/users/${userId}/action`, { action })
      fetchUsers()
      if (selectedUser?.id === userId) {
        handleUserSelect(userId)
      }
    } catch (error) {
      console.error('Failed to perform action:', error)
    }
  }

  const getStatusBadge = (user: AdminUser) => {
    if (user.isBanned) return <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">Banned</span>
    if (user.isSuspended) return <span className="px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-400">Suspended</span>
    if (!user.isActive) return <span className="px-2 py-0.5 rounded text-xs bg-gray-500/20 text-gray-400">Inactive</span>
    return <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">Active</span>
  }

  const getRiskIndicator = (user: AdminUser) => {
    if (user.activeStrikes >= 2) return 'high'
    if (user.violationCount >= 1) return 'medium'
    return 'low'
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Link to="/admin" className="text-gray-400 hover:text-white">
                Admin
              </Link>
              <span className="text-gray-600">/</span>
              <h1 className="text-xl font-bold">User Management</h1>
            </div>
            <p className="text-gray-400 text-sm">{users.length} users</p>
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by username or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-pink-500"
            />
          </div>
          <div className="flex gap-2">
            {(['all', 'active', 'suspended', 'banned', 'high_risk'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg transition ${
                  filter === f
                    ? 'bg-pink-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {f.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User List */}
            <div className="lg:col-span-2">
              <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-700/50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">User</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Age Group</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Status</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Risk</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Stats</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user.id}
                        onClick={() => handleUserSelect(user.id)}
                        className={`border-t border-gray-700 cursor-pointer transition ${
                          selectedUser?.id === user.id
                            ? 'bg-pink-500/10'
                            : 'hover:bg-gray-700/50'
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                              {user.avatarUrl ? (
                                <img src={user.avatarUrl} alt="" className="w-full h-full rounded-full" />
                              ) : (
                                '👤'
                              )}
                            </div>
                            <div>
                              <p className="font-medium flex items-center gap-1">
                                @{user.username}
                                {user.isVerified && <span className="text-blue-400">✓</span>}
                              </p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="capitalize text-sm">{user.ageGroup}</span>
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(user)}</td>
                        <td className="px-4 py-3">
                          <RiskBadge level={getRiskIndicator(user)} strikes={user.activeStrikes} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-400">
                            <p>{user.postCount} posts</p>
                            <p>{user.followerCount} followers</p>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* User Detail Panel */}
            {selectedUser ? (
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 sticky top-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center text-2xl">
                    {selectedUser.avatarUrl ? (
                      <img src={selectedUser.avatarUrl} alt="" className="w-full h-full rounded-full" />
                    ) : (
                      '👤'
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      {selectedUser.displayName}
                      {selectedUser.isVerified && <span className="text-blue-400">✓</span>}
                    </h3>
                    <p className="text-gray-400">@{selectedUser.username}</p>
                  </div>
                </div>

                {/* User Info */}
                <div className="space-y-3 mb-6">
                  <InfoRow label="Email" value={selectedUser.email} />
                  <InfoRow label="Age Group" value={selectedUser.ageGroup} />
                  <InfoRow label="Joined" value={new Date(selectedUser.createdAt).toLocaleDateString()} />
                  <InfoRow label="Last Active" value={new Date(selectedUser.lastActiveAt).toLocaleString()} />
                </div>

                {/* Moderation Stats */}
                <div className="bg-gray-900 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Moderation Stats</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <StatItem label="Content Blocked" value={selectedUser.moderationStats.contentBlocked} />
                    <StatItem label="Content Flagged" value={selectedUser.moderationStats.contentFlagged} />
                    <StatItem label="Reports Received" value={selectedUser.moderationStats.reportsReceived} />
                    <StatItem label="Active Strikes" value={selectedUser.activeStrikes} highlight={selectedUser.activeStrikes > 0} />
                  </div>
                </div>

                {/* Violations */}
                {selectedUser.violations.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-400 mb-3">Recent Violations</h4>
                    <div className="space-y-2">
                      {selectedUser.violations.map((v) => (
                        <div key={v.id} className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                          <div className="flex justify-between items-start">
                            <span className="text-sm font-medium capitalize">{v.type}</span>
                            <span className="text-xs text-gray-500">
                              {new Date(v.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{v.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="border-t border-gray-700 pt-4">
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Actions</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {!selectedUser.isSuspended && !selectedUser.isBanned && (
                      <button
                        onClick={() => handleUserAction(selectedUser.id, 'warn')}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-lg transition text-sm"
                      >
                        ⚠ Warn
                      </button>
                    )}
                    {selectedUser.isSuspended ? (
                      <button
                        onClick={() => handleUserAction(selectedUser.id, 'unsuspend')}
                        className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition text-sm"
                      >
                        ✓ Unsuspend
                      </button>
                    ) : !selectedUser.isBanned && (
                      <button
                        onClick={() => handleUserAction(selectedUser.id, 'suspend')}
                        className="bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg transition text-sm"
                      >
                        🚫 Suspend
                      </button>
                    )}
                    {selectedUser.isBanned ? (
                      <button
                        onClick={() => handleUserAction(selectedUser.id, 'unban')}
                        className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition text-sm"
                      >
                        ✓ Unban
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUserAction(selectedUser.id, 'ban')}
                        className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition text-sm"
                      >
                        ⛔ Ban
                      </button>
                    )}
                    {!selectedUser.isVerified && (
                      <button
                        onClick={() => handleUserAction(selectedUser.id, 'verify')}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition text-sm"
                      >
                        ✓ Verify
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 flex items-center justify-center text-gray-500">
                Select a user to view details
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function RiskBadge({ level, strikes }: { level: 'low' | 'medium' | 'high'; strikes: number }) {
  const colors = {
    low: 'bg-green-500/20 text-green-400',
    medium: 'bg-yellow-500/20 text-yellow-400',
    high: 'bg-red-500/20 text-red-400',
  }

  return (
    <span className={`px-2 py-0.5 rounded text-xs ${colors[level]}`}>
      {level === 'high' ? `⚠ ${strikes} strikes` : level.charAt(0).toUpperCase() + level.slice(1)}
    </span>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-400">{label}</span>
      <span className="capitalize">{value}</span>
    </div>
  )
}

function StatItem({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className="text-center">
      <p className={`text-xl font-bold ${highlight ? 'text-red-400' : ''}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  )
}
